import {
  autoUpdate,
  flip,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { forwardRef, ReactNode, useEffect, useState } from "react";
import ReactDOM from "react-dom";

import { SearchInput, SearchInputProps } from "./search";
import { getLabelMessageId } from "./utils";
import { Dropdown, Item } from "../dropdown";
import { Label } from "../label";

export type SearchFieldInputProps = Omit<SearchInputProps, "message"> & {
  message?: string | ReactNode;
  options?: Item[];
  fetching?: boolean;
};

export const SearchFieldInput = forwardRef<
  HTMLInputElement,
  SearchFieldInputProps
>((props, ref) => {
  const { mandatory, options, fetching, onClear, ...rest } = props;
  const [showOptions, setShowOptions] = useState(false);

  const floatingOptions = useFloating({
    placement: "bottom",
    open: showOptions,
    onOpenChange: setShowOptions,
    middleware: [shift({ padding: 10 }), flip({ fallbackPlacements: ["top"] })],
    whileElementsMounted: autoUpdate,
  });

  const optionsInteractions = useInteractions([
    useDismiss(floatingOptions.context),
  ]);

  useEffect(() => {
    if (fetching) {
      setShowOptions(true);
    }
  }, [fetching]);

  return (
    <Label
      text={props.label}
      mandatory={mandatory}
      disabled={props.disabled}
      name={props.id}
      error={!!props.error}
      message={props.message}
      className="flex-1"
      messageId={getLabelMessageId(props)}
    >
      <div ref={floatingOptions.refs.setReference}>
        <SearchInput
          {...rest}
          message={
            typeof props.message === "string" ? props.message : undefined
          }
          ref={ref}
          onClear={(e) => {
            onClear?.(e);
            setShowOptions(false);
          }}
        />
      </div>
      {showOptions &&
        !!options?.length &&
        // Render the dropdown outside its parent DOM, in the document body, to ensure consistent visibility above other UI components.
        ReactDOM.createPortal(
          <div
            className="z-modal"
            ref={floatingOptions.refs.setFloating}
            style={{
              ...floatingOptions.floatingStyles,
              width: `${(floatingOptions.refs.reference?.current as HTMLDivElement)?.offsetWidth}px`,
            }}
            data-testid={`${props.dataTestId || "search-field"}-options`}
            {...optionsInteractions.getFloatingProps()}
          >
            <Dropdown
              items={options.map((option) => ({
                ...option,
                onClick: (e) => {
                  option.onClick?.(e);
                  setShowOptions(false);
                },
              }))}
              variant="default"
            />
          </div>,
          document.body,
        )}
    </Label>
  );
});
